import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronUp, ChevronRight, X, Clock, Sparkles, LayoutDashboard, CalendarDays, Bell } from "lucide-react";
import api from "../../lib/api";
import { buildSuggestions } from "../../lib/planniRules";
import { useAppearance } from "../../context/AppearanceContext";
import { useIsMobile } from "../../hooks/useViewport";
import { AdaptiveSheet } from "../adaptive/AdaptiveSheet";

// Planni - PlannioLife's calm household guide, now callable from anywhere. Always present as a subtle
// avatar (unless hidden in Appearance). When Planni has a genuinely useful, data-driven suggestion it
// shows a gentle badge; otherwise tapping opens a warm, seasonal greeting with a few quick links.
// Not a chatbot, never intrusive: fade/slide only, dismissals and snoozes persist.
const STORE_KEY = "planni_suggestion_state_v1";
const readState = () => { try { return JSON.parse(localStorage.getItem(STORE_KEY) || "{}"); } catch { return {}; } };
const writeState = (s) => { try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch { /* ignore */ } };

const QUICK_LINKS = [
  { to: "/app/dashboard", label: "Home", icon: LayoutDashboard },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/app/reminders", label: "Reminders", icon: Bell },
];

export default function PlanniTip() {
  const { theme, prefs } = useAppearance();
  const planniSrc = theme?.planni || "/planni.png";
  const thinkSrc = theme?.poses?.think || planniSrc;
  const [suggestions, setSuggestions] = useState([]);
  const [state, setState] = useState(readState());
  const [expanded, setExpanded] = useState(false);
  // Draggable position: distances from the right/bottom viewport edges, persisted across visits.
  const POS_KEY = "planni_pos_v1";
  const [pos, setPos] = useState(() => {
    try { return JSON.parse(localStorage.getItem(POS_KEY) || "null"); } catch { return null; }
  });
  const rootRef = React.useRef(null);
  const dragRef = React.useRef(null);
  const draggedRef = React.useRef(false);

  const clampPos = (p, el) => {
    const w = el?.offsetWidth || 56, h = el?.offsetHeight || 56;
    const maxRight = Math.max(8, window.innerWidth - w - 8);
    const maxBottom = Math.max(8, window.innerHeight - h - 8);
    return { right: Math.min(Math.max(8, p.right), maxRight), bottom: Math.min(Math.max(8, p.bottom), maxBottom) };
  };

  const onDragStart = (e) => {
    if (expanded) return;
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    dragRef.current = {
      startX: pt.clientX, startY: pt.clientY,
      right: window.innerWidth - rect.right, bottom: window.innerHeight - rect.bottom,
      moved: false,
    };
    draggedRef.current = false;
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const p = ev.touches ? ev.touches[0] : ev;
      const dx = p.clientX - d.startX, dy = p.clientY - d.startY;
      if (!d.moved && Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      d.moved = true; draggedRef.current = true;
      if (ev.cancelable) ev.preventDefault();
      setPos(clampPos({ right: d.right - dx, bottom: d.bottom - dy }, el));
    };
    const end = () => {
      const d = dragRef.current;
      dragRef.current = null;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
      if (d?.moved) {
        setPos((p) => { try { localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* ignore */ } return p; });
        setTimeout(() => { draggedRef.current = false; }, 0);
      }
    };
    if (e.type === "touchstart") {
      window.addEventListener("touchmove", move, { passive: false });
      window.addEventListener("touchend", end);
    } else {
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", end);
    }
  };

  // Keep Planni fully on screen if the viewport shrinks (rotation, resize).
  useEffect(() => {
    if (!pos) return undefined;
    const onResize = () => setPos((p) => (p ? clampPos(p, rootRef.current) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!pos]);

  const [sessionHidden, setSessionHidden] = useState(() => {
    try { return sessionStorage.getItem("planni_session_hidden") === "1"; } catch { return false; }
  });
  const hideForSession = () => {
    try { sessionStorage.setItem("planni_session_hidden", "1"); } catch { /* ignore */ }
    setExpanded(false); setSessionHidden(true);
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/planni/context").then((r) => {
      if (cancelled) return;
      const data = r.data || {};
      const HKEY = "planni_health_overall_v1";
      let prev = null;
      try { const v = localStorage.getItem(HKEY); prev = v === null ? null : Number(v); } catch { /* ignore */ }
      const overall = data.health?.overall;
      if (typeof overall === "number") { data.health_prev = prev; try { localStorage.setItem(HKEY, String(overall)); } catch { /* ignore */ } }
      setSuggestions(buildSuggestions({ data }));
    }).catch(() => { if (!cancelled) setSuggestions(buildSuggestions({ data: {} })); });
    return () => { cancelled = true; };
  }, []);

  // Ranked "next best actions": everything not dismissed or snoozed, in rule priority order.
  const visible = useMemo(() => {
    const now = Date.now();
    return suggestions.filter((s) => {
      const st = state[s.id];
      if (!st) return true;
      if (st.dismissed) return false;
      if (st.snoozeUntil && st.snoozeUntil > now) return false;
      return true;
    });
  }, [suggestions, state]);
  const active = visible[0];
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  if (prefs?.planniVisibility === "hidden") return null;
  // Dismissed for this browsing session only - Planni returns next session (unless hidden in prefs).
  if (sessionHidden) return null;
  // "Reduced" suppresses the proactive badge but keeps Planni reachable on demand.
  const showBadge = prefs?.planniVisibility !== "reduced" && !!active;

  const update = (patch) => {
    if (!active) return;
    const next = { ...state, [active.id]: { ...(state[active.id] || {}), ...patch } };
    setState(next); writeState(next); setExpanded(false);
  };
  const snooze = () => update({ snoozeUntil: Date.now() + 3 * 86400000 });
  const dismiss = () => update({ dismissed: true });

  return (
    <div ref={rootRef} className="fixed bottom-5 right-5 z-[80] max-w-[calc(100vw-2.5rem)]" data-testid="planni-tip"
      style={pos ? { bottom: `${pos.bottom}px`, right: `${pos.right}px`, touchAction: "none" } : {
        // Mobile: sit above the bottom tab bar (3.75rem) and above any sticky action bar
        // (live --pl-sticky-offset variable) so Planni never blocks a primary action.
        bottom: window.matchMedia && window.matchMedia("(max-width: 767px)").matches
          ? "calc(4.75rem + var(--pl-sticky-offset, 0rem) + env(safe-area-inset-bottom, 0px))"
          : "calc(1.25rem + env(safe-area-inset-bottom, 0px))",
        right: "calc(1.25rem + env(safe-area-inset-right, 0px))",
      }}>
      {(!expanded || isMobile) ? (
        <div className="relative" onPointerDown={onDragStart} onTouchStart={onDragStart} data-testid="planni-drag-handle">
          <button data-testid="planni-collapsed" aria-label="Open Planni" onClick={() => { if (!draggedRef.current) setExpanded(true); }}
            className={`group flex items-center gap-2.5 rounded-full border border-[#E2E8F0] bg-white/95 shadow-[0_10px_30px_rgba(45,52,54,0.14)] backdrop-blur transition-all duration-300 hover:shadow-[0_14px_38px_rgba(45,52,54,0.2)] ${active ? "py-2 pl-2 pr-3.5" : "p-2"} sm:py-2 sm:pl-2 sm:pr-4`}>
            <span className="relative">
              <img src={active ? thinkSrc : planniSrc} alt="Planni" className="pl-planni-idle h-9 w-9 rounded-full object-cover" />
              {showBadge && <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#3B82F6]" data-testid="planni-badge" />}
            </span>
            <span className={`${active ? "block" : "hidden"} text-left sm:block`}>
              <span className="block text-xs font-semibold text-[#2D3436]">{active ? "Planni has a suggestion" : "Ask Planni"}</span>
              <span className="block max-w-[9.5rem] truncate text-xs text-[#636E72] sm:max-w-[13rem]">{active ? active.title : "Here whenever you need a hand"}</span>
            </span>
            <ChevronUp className="hidden h-4 w-4 text-[#7f8985] transition-transform group-hover:-translate-y-0.5 sm:block" />
          </button>
          <button data-testid="planni-hide-session" aria-label="Hide Planni for now"
            title="Hide Planni for now. He'll be back next visit, or switch him off in Settings, then Appearance."
            onClick={(e) => { e.stopPropagation(); hideForSession(); }}
            className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#7f8985] shadow-sm transition-colors hover:text-[#2D3436]">
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div data-testid="planni-expanded" className="w-80 origin-bottom-right animate-[planniIn_.25s_ease-out] overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white shadow-[0_20px_50px_rgba(45,52,54,0.2)]">
          <div className="flex items-start gap-3 p-4 theme-band">
            <img src={active ? thinkSrc : planniSrc} alt="Planni" className="h-12 w-12 shrink-0 rounded-full object-cover" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#2D3436]">Hi, I'm Planni</p>
              <p className="mt-0.5 text-xs text-[#636E72]">{active ? active.title : theme?.greeting}</p>
            </div>
            <button data-testid="planni-dismiss" onClick={() => (active ? dismiss() : setExpanded(false))} aria-label="Close" className="text-[#7f8985] hover:text-[#2D3436]"><X className="h-4 w-4" /></button>
          </div>
          {active ? (
            <>
              <p className="px-4 pb-3 pt-3 text-sm leading-relaxed text-[#636E72]">{active.body}</p>
              <div className="flex flex-wrap items-center gap-2 border-t border-[#F0F1EE] p-3">
                {active.action && (
                  <Link data-testid="planni-action" to={active.action.to} onClick={dismiss}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#0F766E] px-3.5 py-2 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5">
                    <Sparkles className="h-3.5 w-3.5" /> {active.action.label}
                  </Link>
                )}
                <button data-testid="planni-snooze" onClick={snooze} className="inline-flex items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#636E72] hover:bg-[#f0f1ee]"><Clock className="h-3.5 w-3.5" /> Remind me later</button>
                <button onClick={dismiss} className="inline-flex items-center rounded-xl px-3 py-2 text-xs font-medium text-[#7f8985] hover:text-[#2D3436]">Dismiss</button>
              </div>
            </>
          ) : (
            <div className="p-4">
              <p className="text-sm leading-relaxed text-[#636E72]">All clear right now. I'll pop up if anything needs your attention. In the meantime, jump to:</p>
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="planni-quick-links">
                {QUICK_LINKS.map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setExpanded(false)}
                    className="flex flex-col items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-2 py-3 text-xs font-medium text-[#636E72] transition-colors hover:bg-[#0F766E]/5">
                    <l.icon className="h-4 w-4 text-[#0F766E]" /> {l.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Mobile: Planni speaks through a calm bottom sheet, the guided companion voice */}
      <AdaptiveSheet open={isMobile && expanded} onOpenChange={(v) => !v && setExpanded(false)} testid="planni-sheet">
        <div className="flex items-start gap-3 pb-3">
          <img src={active ? thinkSrc : planniSrc} alt="Planni" className="h-12 w-12 shrink-0 rounded-full object-cover" />
          <div className="min-w-0 flex-1 pt-1">
            <p className="text-sm font-semibold text-[#2D3436]">Hi, I'm Planni</p>
            <p className="mt-0.5 text-xs text-[#636E72]">{active ? "One thing worth a look" : theme?.greeting}</p>
          </div>
        </div>
        {active ? (
          <div className="pb-2">
            <div className="rounded-2xl bg-[#F8F9F6] px-4 py-3.5" data-testid="planni-sheet-active">
              <p className="text-sm font-semibold text-[#2D3436]">{active.title}</p>
              <p className="mt-1 text-sm leading-relaxed text-[#636E72]">{active.body}</p>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {active.action && (
                <button data-testid="planni-sheet-action" onClick={() => { const to = active.action.to; dismiss(); navigate(to); }}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#0F766E] px-4 py-2.5 text-sm font-semibold text-white active:scale-[0.98]">
                  <Sparkles className="h-4 w-4" /> {active.action.label}
                </button>
              )}
              <button data-testid="planni-sheet-snooze" onClick={snooze} className="inline-flex items-center gap-1.5 rounded-full border border-[#EBECE8] px-3.5 py-2.5 text-sm font-medium text-[#636E72]"><Clock className="h-4 w-4" /> Later</button>
              <button data-testid="planni-sheet-dismiss" onClick={dismiss} className="inline-flex items-center rounded-full px-3 py-2.5 text-sm font-medium text-[#7f8985]">Dismiss</button>
            </div>
            {visible.length > 1 && (
              <div className="mt-4">
                <p className="mb-2 px-1 text-xs font-medium uppercase tracking-wide text-[#7f8985]">Also worth a look</p>
                <div className="overflow-hidden rounded-2xl border border-[#EBECE8]" data-testid="planni-sheet-more">
                  {visible.slice(1, 3).map((s) => (
                    <button key={s.id} onClick={() => { setExpanded(false); if (s.action?.to) navigate(s.action.to); }}
                      className="flex w-full items-center gap-3 border-t border-[#F0F1EE] px-4 py-3 text-left first:border-t-0">
                      <span className="min-w-0 flex-1 truncate text-sm text-[#2D3436]">{s.title}</span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[#c3c9c5]" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="pb-2">
            <p className="text-sm leading-relaxed text-[#636E72]">All clear right now. I'll pop up if anything needs your attention. In the meantime, jump to:</p>
            <div className="mt-3 grid grid-cols-3 gap-2" data-testid="planni-sheet-quick-links">
              {QUICK_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setExpanded(false)}
                  className="flex flex-col items-center gap-1.5 rounded-xl border border-[#E2E8F0] px-2 py-3 text-xs font-medium text-[#636E72]">
                  <l.icon className="h-4 w-4 text-[#0F766E]" /> {l.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </AdaptiveSheet>
      <style>{`@keyframes planniIn{from{opacity:0;transform:translateY(8px) scale(.98)}to{opacity:1;transform:translateY(0) scale(1)}}`}</style>
    </div>
  );
}
